import type {
  AutomationConfig,
  TriggerConfig,
} from "@smartsuite/automation-engine-api";
import { AutomationConfig_UserStatus } from "@smartsuite/automation-engine-api";
import * as R from "remeda";
import { t } from "i18next";
import { useState, useEffect } from "react";
import { PhraseBuilder } from "../../../../components/PhraseBuilder";
import { IntegrationsIconList } from "../../IntegrationsIconList";
import { Description } from "./Description";
import {
  StyledAutomationCard,
  StyledAutomationCardControls,
  StyledAutomationCardHead,
  StyledAutomationCardContent,
  StyledDotWrapper,
  StyledSwitchWrapper,
  StyledErrorMessageLabel,
  DescriptionContainer,
} from "./style";
import { observer } from "mobx-react-lite";
import { useRootStore } from "../../../../stores/hooks";
import { useEngineClient } from "../../../../services/engineClient";
import type { RootStore } from "../../../../stores";
import type { EngineClient } from "../../../../services/engineClient";
import { getLocalCssColorVariables } from "../../../../utils/cssVariablesUtils";
import { DuplicateModal } from "../../../AutomationForm/DuplicateModal";
import { AutomationCardError } from "./AutomationCardError";
import {
  FlashNotificationPosition,
  notifySuccess,
} from "@smartsuite/flash-notifications";
import {
  DotsMenu,
  Icon,
  MenuOption,
  Paragraph,
  SwitcherAutomation,
} from "@smartsuite/react-ui/lib";
import {
  Colors,
  SolutionColorVariables,
  TextColorNames,
} from "@smartsuite/types";
import { duplicateInput } from "../../../../utils/duplicate-input";

export const AutomationCard = observer<{
  automation: AutomationConfig;
  editAutomation: (automation: AutomationConfig) => void;
  onDelete: (automation: AutomationConfig) => void;
  toggleStatus: (checked: boolean, automation: AutomationConfig) => void;
}>(({ automation, editAutomation, onDelete, toggleStatus }) => {
  const store = useRootStore();

  const [isEdit, setIsEdit] = useState(false);

  const [isOpenDotMenu, setOpenDotMenu] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const engineClient = useEngineClient();
  const automationLabel = store.automations.list.find(
    (a) => a.automationId === automation.automationId,
  )?.label;
  const [isDuplicating, setIsDuplicating] = useState(false);

  useEffect(() => {
    if (
      automation.systemStatus?.errors.some((e) =>
        ["invalidCredential", "fieldMissing", "inputMissing"].includes(
          e.error.oneofKind ?? "",
        ),
      )
    ) {
      setErrorMessage(t("automation-card.configuration-error"));
    } else {
      setErrorMessage("");
    }
  }, [automation.systemStatus]);

  const checkStatus = (automation: AutomationConfig): boolean => {
    return automation.userStatus === AutomationConfig_UserStatus.USER_ENABLED;
  };

  const onCopyLink = (automationId: string): void => {
    void window.navigator.clipboard
      .writeText(window.location.href.replace("list", automationId))
      .then(() => {
        notifySuccess(
          "Link copied",
          FlashNotificationPosition.bottomLeft,
          3000,
        );
      });
  };

  const isPermitted =
    automation.systemStatus?.systemStatus.oneofKind !== "notPermitted";

  return (
    <StyledAutomationCard
      hasError={R.isTruthy(errorMessage)}
      onClick={(e) => {
        editAutomation(automation);
      }}
      key={automation.automationId}
      isPermitted={isPermitted}
    >
      {/* Header */}
      <StyledAutomationCardHead>
        <IntegrationsIconList automation={automation} />
        <Paragraph
          weight="semibold"
          styles={{
            fontSize: "18px",
            lineHeight: "25px",
            position: "absolute",
            left: 152,
            zIndex: 1,
          }}
        >
          {automationLabel}
        </Paragraph>
        {R.isTruthy(errorMessage) && (
          <StyledErrorMessageLabel>
            <Icon
              size={"S"}
              name={"info-filled"}
              color={TextColorNames.white}
            />
            {errorMessage}
          </StyledErrorMessageLabel>
        )}
        <StyledAutomationCardControls
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          {/* Dot menu */}
          <StyledDotWrapper className="hover-dots" isOpen={isOpenDotMenu}>
            <DotsMenu
              opened={isOpenDotMenu}
              onToggle={setOpenDotMenu}
              size="s"
              minWidth={200}
              at="bottom"
              my="top+12"
              smallListPaddings={true}
              backgroundColors={{
                hover: SolutionColorVariables.sc2,
                active: SolutionColorVariables.sc2,
              }}
              colors={{
                default: SolutionColorVariables.sc1,
                hover: SolutionColorVariables.sc1,
                active: SolutionColorVariables.sc1,
              }}
              portalStyles={getLocalCssColorVariables(store.theme)}
            >
              <MenuOption
                key="copy-link"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenDotMenu(false);
                  onCopyLink(automation.automationId);
                }}
                iconName={"link"}
                iconColor={SolutionColorVariables.sc1}
                iconSize={"S"}
                iconSmallMargin
              >
                {t("3dot-menu.copy-link")}
              </MenuOption>
              <MenuOption
                key="duplicate-automation"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenDotMenu(false);
                  setIsDuplicating(true);
                }}
                iconName={"layers"}
                iconColor={SolutionColorVariables.sc1}
                iconSize={"S"}
                iconSmallMargin
                disabled={store.automations.isLimitReached}
                tooltipText={
                  store.automations.isLimitReached
                    ? t("automation-list.reached-max-automations", {
                        number:
                          store.featureFlags.limits.maxAutomationsPerSolution,
                      })
                    : undefined
                }
              >
                {t("3dot-menu.duplicate-automation")}
              </MenuOption>
              <MenuOption
                key="edit-description"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenDotMenu(false);
                  setIsEdit(true);
                }}
                iconName={"edit"}
                iconColor={SolutionColorVariables.sc1}
                iconSize={"S"}
                iconSmallMargin
              >
                {t(
                  automation.description
                    ? "3dot-menu.edit-description"
                    : "3dot-menu.add-description",
                )}
              </MenuOption>
              <MenuOption
                key="delete-automation"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenDotMenu(false);
                  onDelete(automation);
                }}
                iconName={"trash-1"}
                iconColor={Colors.bRed}
                iconSize={"S"}
                iconSmallMargin
              >
                {t("3dot-menu.delete-automation")}
              </MenuOption>
            </DotsMenu>
          </StyledDotWrapper>

          {/* Switch button */}
          <StyledSwitchWrapper>
            <SwitcherAutomation
              checked={checkStatus(automation)}
              onChange={(e) => {
                toggleStatus(e, automation);
              }}
            />
          </StyledSwitchWrapper>
        </StyledAutomationCardControls>
      </StyledAutomationCardHead>
      <StyledAutomationCardContent>
        {/* Component Phrase Builder */}

        <DescriptionContainer>
          {automation.description || isEdit ? (
            <Description
              automation={automation}
              isEdit={isEdit}
              setIsEdit={setIsEdit}
            />
          ) : (
            <PhraseBuilder automation={automation} />
          )}
        </DescriptionContainer>
      </StyledAutomationCardContent>
      <AutomationCardError
        systemStatus={automation.systemStatus}
      ></AutomationCardError>
      {isDuplicating && (
        <DuplicateModal
          label={automation.label}
          onCancel={() => {
            setIsDuplicating(false);
          }}
          onDuplicate={(newLabel: string) => {
            setIsDuplicating(false);
            createDuplicate(engineClient, store, automation, newLabel);
          }}
        />
      )}
    </StyledAutomationCard>
  );
});

const createDuplicate = (
  engineClient: EngineClient,
  store: RootStore,
  automation: AutomationConfig,
  newLabel: string,
): void => {
  let duplicatedAutomation = {
    ...automation,
    automationId: "",
    label: newLabel,
  };

  // @ts-expect-error all properties are present
  const trigger: TriggerConfig | undefined = {
    ...automation.trigger,
    inputs:
      automation.trigger?.inputs.map((input) => {
        return duplicateInput(input);
      }) ?? [],
  };
  duplicatedAutomation = {
    ...duplicatedAutomation,
    trigger,
  };

  void engineClient
    .createAutomation({ automation: duplicatedAutomation })
    .then(({ automationId, firstCreated }) => {
      const createdAutomation = {
        ...duplicatedAutomation,
        automationId,
        firstCreated,
        lastUpdated: undefined,
      };
      store.automations.addItem(createdAutomation);
    });
};
